import { useCallback, useState } from 'react';
import {
  Stack,
  Text,
  Strong,
  Secondary,
  Inline,
  InlineItem,
  Button,
  Select,
  Content,
  Tooltip,
  useToast,
} from '@kinesis/bungle';
import { addDays, addYears, parseISO, differenceInDays } from 'date-fns/fp';
import { noop } from 'lodash/fp';
import { formatDateString, formatDistanceStrings } from '@/date';
import { summariseScopes } from '@/scopes';
import { ScopePicker } from '@/components/scope-picker';
import { Layout } from '@/components/layout';
import { AccessKey } from '@/types';

const expirationOptions = [
  { label: 'No expiration', value: 'none' },
  { label: '30 days from now', value: '30' },
  { label: '60 days from now', value: '60' },
  { label: '90 days from now', value: '90' },
  { label: '1 year from now', value: '365' },
];

const computeExpiresAt = (option: string): string | null => {
  if (option === 'none') return null;
  const days = Number(option);
  if (days === 365) return addYears(1, new Date()).toISOString();
  return addDays(days, new Date()).toISOString();
};

const formatExpirationStatus = (
  expiresAt: string | null | undefined,
  now?: string,
) => {
  if (!expiresAt) return 'No expiration';
  const expiryDate = parseISO(expiresAt);
  const nowDate = now ? parseISO(now) : new Date();
  if (expiryDate <= nowDate) return 'Expired';
  return `${formatDateString(expiresAt)} (${formatDistanceStrings(now ?? new Date().toISOString(), expiresAt)})`;
};

const AccessKeyDetail = (props: AccessKeyDetailProps) => {
  const {
    accessKey,
    now,
    onUpdate = noop,
    onBack = noop,
    isUpdating = false,
  } = props;
  const [scopes, setScopes] = useState(accessKey.scopes);
  const [expirationSelection, setExpirationSelection] = useState<
    string | undefined
  >(undefined);
  const toast = useToast('globalTop');

  const hasScopeChanges =
    JSON.stringify(scopes.slice().sort()) !==
    JSON.stringify(accessKey.scopes.slice().sort());
  const hasExpirationChanges = expirationSelection != null;
  const hasChanges = hasScopeChanges || hasExpirationChanges;

  const onSave = useCallback(async () => {
    const expiresAt = hasExpirationChanges
      ? computeExpiresAt(expirationSelection!)
      : undefined;
    await onUpdate(scopes, expiresAt);
    setExpirationSelection(undefined);
    toast('Key updated.', { variant: 'success', duration: 3000 });
  }, [scopes, expirationSelection, hasExpirationChanges, onUpdate, toast]);

  const onReset = useCallback(() => {
    setScopes(accessKey.scopes);
    setExpirationSelection(undefined);
  }, [accessKey.scopes]);

  const date = formatDateString(accessKey.created);
  const shortId = accessKey.id.slice(0, 12);
  const isExpired =
    accessKey.expiresAt != null &&
    now != null &&
    parseISO(accessKey.expiresAt) <= parseISO(now);
  const isOldWithoutExpiration =
    accessKey.expiresAt == null &&
    now != null &&
    differenceInDays(parseISO(accessKey.created), parseISO(now)) >= 365;

  return (
    <Layout variant='narrow'>
      <Content>
        <Stack space='medium'>
          <Inline>
            <InlineItem>
              <Button variant='secondary' icon='left' onClick={onBack}>
                Back
              </Button>
            </InlineItem>
          </Inline>

          {accessKey.name && (
            <Stack space='small'>
              <Text>
                <Strong>Name</Strong>
              </Text>
              <Text>{accessKey.name}</Text>
            </Stack>
          )}

          <Stack space='small'>
            <Text>
              <Strong>Key ID</Strong>
            </Text>
            <Text>
              <Tooltip title={accessKey.id} placement='top'>
                <span>
                  <Secondary>{shortId}...</Secondary>
                </span>
              </Tooltip>
            </Text>
          </Stack>

          <Stack space='small'>
            <Text>
              <Strong>Created</Strong>
            </Text>
            <Text>
              <Secondary>{date}</Secondary>
            </Text>
          </Stack>

          <Stack space='small'>
            <Text>
              <Strong>Last used</Strong>
            </Text>
            <Text>
              <Secondary>
                {accessKey.lastUsed
                  ? now
                    ? `${formatDateString(accessKey.lastUsed)} (${formatDistanceStrings(now, accessKey.lastUsed)})`
                    : formatDateString(accessKey.lastUsed)
                  : 'Never used'}
              </Secondary>
            </Text>
          </Stack>

          <Stack space='small'>
            <Text>
              <Strong>Expiration</Strong>
            </Text>
            <Text>
              <Secondary>
                <span
                  style={
                    isExpired
                      ? { color: 'var(--color-text-danger, #dc2626)' }
                      : isOldWithoutExpiration
                        ? { color: 'var(--color-text-warning, #b45309)' }
                        : undefined
                  }
                >
                  {formatExpirationStatus(accessKey.expiresAt, now)}
                </span>
                {isOldWithoutExpiration && (
                  <span style={{ color: 'var(--color-text-warning, #b45309)' }}>
                    {' '}
                    — consider setting an expiration
                  </span>
                )}
              </Secondary>
            </Text>
            <Select
              label='Update expiration'
              magnitude='large'
              options={expirationOptions}
              value={expirationSelection ?? 'none'}
              onChange={setExpirationSelection}
            />
          </Stack>

          <Stack space='small'>
            <Text>
              <Strong>Current scopes</Strong>
            </Text>
            <Text>
              <Secondary>{summariseScopes(accessKey.scopes)}</Secondary>
            </Text>
          </Stack>

          <ScopePicker value={scopes} onChange={setScopes} />

          {hasChanges && (
            <Inline space='small'>
              <InlineItem sizing='fill-container' />
              <InlineItem>
                <Button variant='secondary' onClick={onReset}>
                  Reset
                </Button>
              </InlineItem>
              <InlineItem>
                <Button variant='primary' onClick={onSave} loading={isUpdating}>
                  Update key
                </Button>
              </InlineItem>
            </Inline>
          )}
        </Stack>
      </Content>
    </Layout>
  );
};

type AccessKeyDetailProps = {
  accessKey: AccessKey;
  now?: string;
  onUpdate?: (
    scopes: string[],
    expiresAt?: string | null,
  ) => Promise<void> | void;
  onBack?: () => void;
  isUpdating?: boolean;
};

export type { AccessKeyDetailProps };
export { AccessKeyDetail };
